import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const testimonialSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: "Name must be at least 2 characters" })
    .max(100, { message: "Name must be less than 100 characters" }),
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  role: z
    .string()
    .trim()
    .max(100, { message: "Role must be less than 100 characters" })
    .optional(),
  category: z.string().optional(),
  testimonial: z
    .string()
    .trim()
    .min(20, { message: "Please share at least 20 characters about your experience" })
    .max(2000, { message: "Testimonial must be less than 2000 characters" }),
});

type TestimonialFormData = z.infer<typeof testimonialSchema>;

export function ShareYourStoryForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const form = useForm<TestimonialFormData>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "",
      category: "",
      testimonial: "",
    },
  });

  const onSubmit = async (data: TestimonialFormData) => {
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from("testimonials").insert({
        name: data.name,
        email: data.email,
        role: data.role || null,
        category: data.category || null,
        testimonial: data.testimonial,
      });

      if (error) throw error;

      // Navigate to thank you page
      navigate("/thank-you");
    } catch (error) {
      console.error("Error submitting testimonial:", error);
      toast.error("Something went wrong", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-slate-900/70 border border-emerald-300/20 rounded-2xl p-6 md:p-8 shadow-[0_18px_55px_rgba(15,23,42,0.9)] backdrop-blur-md">
      <div className="mb-6">
        <h3 className="text-2xl font-semibold text-emerald-300 tracking-wide mb-2">
          Share your story
        </h3>
        <p className="text-sm text-slate-300/85">
          Your experience could help others discover how Samboards might support them. All testimonials are reviewed before publishing.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-200">Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Your name"
                      {...field}
                      className="bg-slate-800/50 border-slate-700/60 text-slate-100 placeholder:text-slate-500 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-200">Email *</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="your.email@example.com"
                      {...field}
                      className="bg-slate-800/50 border-slate-700/60 text-slate-100 placeholder:text-slate-500 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-200">Role or context (optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Teacher, NHS Staff, Parent"
                      {...field}
                      className="bg-slate-800/50 border-slate-700/60 text-slate-100 placeholder:text-slate-500 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-200">Category (optional)</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-slate-800/50 border-slate-700/60 text-slate-100 focus:border-emerald-400/50 focus:ring-emerald-400/20">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-slate-800 border-slate-700 text-slate-100 z-50">
                      <SelectItem value="everyday" className="hover:bg-slate-700 focus:bg-slate-700">
                        Everyday calm & anxiety
                      </SelectItem>
                      <SelectItem value="nhs" className="hover:bg-slate-700 focus:bg-slate-700">
                        NHS & healthcare
                      </SelectItem>
                      <SelectItem value="education" className="hover:bg-slate-700 focus:bg-slate-700">
                        Schools & education
                      </SelectItem>
                      <SelectItem value="rehab" className="hover:bg-slate-700 focus:bg-slate-700">
                        Rehabilitation
                      </SelectItem>
                      <SelectItem value="workplace" className="hover:bg-slate-700 focus:bg-slate-700">
                        Workplace
                      </SelectItem>
                      <SelectItem value="other" className="hover:bg-slate-700 focus:bg-slate-700">
                        Other
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="testimonial"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-slate-200">Your experience *</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Tell us how Samboards have helped you or someone you care for..."
                    rows={6}
                    {...field}
                    className="bg-slate-800/50 border-slate-700/60 text-slate-100 placeholder:text-slate-500 focus:border-emerald-400/50 focus:ring-emerald-400/20 resize-none"
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
                <p className="text-xs text-slate-400 mt-1">
                  {field.value.length} / 2000 characters
                </p>
              </FormItem>
            )}
          />

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-slate-400">
              * Required fields
            </p>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-jade-light to-jade-dark text-white hover:shadow-[0_0_25px_rgba(114,227,179,0.4)] rounded-button shadow-soft hover:shadow-medium"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit your story"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
